import { useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { Box } from '@pocketlaw/tetris';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useUserInvites, useRole, useCompanyUserContext } from 'app/domains/users';
import { ScreenLayout } from 'shared/domains/common-ui/ScreenLayout';
import { PageTab, PageTabs } from 'shared/domains/common-ui/components/PageTabs';
import { SSORestricted } from 'shared/domains/users/components/SSORestricted';
import { useTeamsContext } from './components/TeamsProvider';
import { UserTeamBreadcrumbs } from './components/UserTeamBreadcrumbs';
import { UsersAndTeamsActions } from './components/UsersAndTeamsActions';
import { InvitesTable } from './screens/InvitesTable';
import { TeamProfilePage } from './screens/TeamProfilePage';
import { TeamsTable } from './screens/TeamsTable';
import { UsersTable } from './screens/UsersTable';
import { TableToolbar } from '../TableToolbar';
import { UsersAndTeamsPaths } from './types';
export function UsersTeamsPage() {
    const { url } = useRouteMatch();
    const role = useRole();
    const location = useLocation();
    const canInvite = role.can('invite:user');
    const { refetch, setSearch: setInviteSearch, searching: invitesSearching, searchValue: inviteSearchValue, } = useUserInvites();
    const { setSearch: setUserSearch, searching: userSearching, searchValue: userSearchValue } = useCompanyUserContext();
    const { setSearch: setTeamsSearch, searching: teamsSearching, searchValue: teamsSearchValue } = useTeamsContext();
    useEffect(() => {
        setUserSearch('');
        setInviteSearch('');
        setTeamsSearch('');
    }, [location, setInviteSearch, setTeamsSearch, setUserSearch]);
    return (<ScreenLayout.Root id="users-teams-management" topbar={<ScreenLayout.Topbar breadcrumbs={<UserTeamBreadcrumbs baseUrl={url}/>} actions={<UsersAndTeamsActions baseUrl={url} refetch={refetch}/>}/>} contentBar={<ScreenLayout.ContentBar>
          <PageTabs>
            <PageTab path={`${url}/${UsersAndTeamsPaths.users}`}>
              <Trans comment="users & teams route - users tab">Users</Trans>
            </PageTab>
            <PageTab path={`${url}/${UsersAndTeamsPaths.teams}`}>
              <Trans comment="users & teams route - teams tab">Teams</Trans>
            </PageTab>
            {canInvite && (<SSORestricted>
                <PageTab path={`${url}/${UsersAndTeamsPaths.invites}`}>
                  <Trans comment="users & teams route - pending invites tab">Pending invites</Trans>
                </PageTab>
              </SSORestricted>)}
          </PageTabs>
        </ScreenLayout.ContentBar>} content={<ScreenLayout.Content>
          <Box alignItems="center" gap="md" mb="lg">
            <Box ml="auto">
              <Switch>
                <Route path={`${url}/${UsersAndTeamsPaths.users}`}>
                  <TableToolbar onSearch={setUserSearch} loading={userSearching} value={userSearchValue}/>
                </Route>

                <Route path={`${url}/${UsersAndTeamsPaths.invites}`}>
                  <SSORestricted>
                    <TableToolbar onSearch={setInviteSearch} loading={invitesSearching} value={inviteSearchValue}/>
                  </SSORestricted>
                </Route>

                <Route exact path={`${url}/${UsersAndTeamsPaths.teams}`}>
                  <TableToolbar onSearch={setTeamsSearch} loading={teamsSearching} value={teamsSearchValue}/>
                </Route>
              </Switch>
            </Box>
          </Box>

          <Switch>
            <Route path={`${url}/${UsersAndTeamsPaths.users}`}>
              <UsersTable />
            </Route>

            <Route path={`${url}/${UsersAndTeamsPaths.invites}`}>
              <SSORestricted>
                <InvitesTable />
              </SSORestricted>
            </Route>

            <Route exact path={`${url}/${UsersAndTeamsPaths.teams}`}>
              <TeamsTable />
            </Route>

            <Route path={`${url}/${UsersAndTeamsPaths.teams}/:teamId`}>
              <TeamProfilePage />
            </Route>

            <Redirect to={`${url}/${UsersAndTeamsPaths.users}`}/>
          </Switch>
        </ScreenLayout.Content>}/>);
}
